/**
 * Material Service
 * Handles API requests related to materials management
 */
import apiClient from './apiClient';

// Define the base URL for material service
const baseUrl = '/api/supplier/material';

const materialService = {
  /**
   * Get all materials
   * @returns {Promise} - Response with materials list
   */
  getAllMaterials() {
    return apiClient.get(baseUrl)
      .then(response => response.data);
  },

  /**
   * Get a material by ID
   * @param {string} id - Material ID
   * @returns {Promise} - Response with material details
   */
  getMaterialById(id) {
    if (!id) {
      console.error('getMaterialById: id is required');
      return Promise.reject(new Error('Material ID is required'));
    }
    
    return apiClient.get(`${baseUrl}/${id}`)
      .then(response => response.data);
  },

  /**
   * Create a new material
   * @param {Object} material - Material data
   * @returns {Promise} - Response with created material
   */
  createMaterial(material) {
    if (!material) {
      console.error('createMaterial: material data is required');
      return Promise.reject(new Error('Material data is required'));
    }
    
    return apiClient.post(baseUrl, material)
      .then(response => response.data);
  },

  /**
   * Update an existing material
   * @param {string} id - Material ID
   * @param {Object} material - Updated material data
   * @returns {Promise} - Response with updated material
   */
  updateMaterial(id, material) {
    if (!id) {
      console.error('updateMaterial: id is required');
      return Promise.reject(new Error('Material ID is required'));
    }
    
    if (!material) {
      console.error('updateMaterial: material data is required');
      return Promise.reject(new Error('Material data is required'));
    }
    
    return apiClient.put(`${baseUrl}/${id}`, material)
      .then(response => response.data);
  },

  /**
   * Search for materials
   * @param {Object} searchParams - Search parameters
   * @returns {Promise} - Response with search results
   */
  searchMaterials(searchParams = {}) {
    return apiClient.post(`${baseUrl}/search`, searchParams)
      .then(response => response.data);
  },

  /**
   * Get child materials for a parent material
   * @param {string} parentId - Parent material ID
   * @returns {Promise} - Response with child materials
   */
  getChildMaterials(parentId) {
    if (!parentId) {
      console.error('getChildMaterials: parentId is required');
      return Promise.reject(new Error('Parent ID is required'));
    }
    
    return apiClient.get(`${baseUrl}/children/${parentId}`)
      .then(response => response.data);
  },

  /**
   * Check if a material code is unique
   * @param {string} id - Material ID
   * @param {string} code - Material code to check
   * @returns {Promise} - Response with uniqueness result
   */
  isCodeUnique(id, code) {
    if (!id) {
      console.error('isCodeUnique: id is required');
      return Promise.reject(new Error('ID is required'));
    }
    
    if (!code) {
      console.error('isCodeUnique: code is required');
      return Promise.reject(new Error('Material code is required'));
    }
    
    return apiClient.post(`${baseUrl}/${id}/is-code-unique`, null, {
      params: { code }
    }).then(response => response.data);
  },

  /**
   * Delete a material by ID
   * @param {string} id - Material ID to delete
   * @returns {Promise} - Response from the delete operation
   */
  deleteMaterial(id) {
    if (!id) {
      console.error('deleteMaterial: id is required');
      return Promise.reject(new Error('Material ID is required for deletion'));
    }
    
    return apiClient.delete(`${baseUrl}/${id}`)
      .then(response => response.data);
  }
};

export default materialService; 